import React, { useState, useRef, useEffect } from 'react'
import { Button, Input, InputGroup, ButtonGroup } from 'reactstrap'
import moment from 'moment'
import FileUpload from '../../components/Chat/FileUpload'
import CannedResponseSelector from '../../components/Chat/CannedResponseSelector'
import TransferModal from '../../components/Chat/TransferModal'
import TicketForm from '../../components/Chat/TicketForm'

const MessagePanel = ({ conversation, onSendMessage, onCloseConversation, onTransferConversation, typingUsers, onSetTyping }) => {
    const [messageInput, setMessageInput] = useState('')
    const [attachments, setAttachments] = useState([])
    const [showTransferModal, setShowTransferModal] = useState(false)
    const [showTicketForm, setShowTicketForm] = useState(false)

    // Focus logic removed to prevent focus stealing bug
    const messagesEndRef = useRef(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }

    useEffect(() => {
        scrollToBottom()
    }, [conversation?.messages])

    // Debounced typing indicator
    useEffect(() => {
        if (!conversation || !onSetTyping) return

        let typingTimeout

        if (messageInput.trim()) {
            // User is typing
            onSetTyping(conversation.id, true)

            // Stop typing after 3 seconds of inactivity
            typingTimeout = setTimeout(() => {
                onSetTyping(conversation.id, false)
            }, 3000)
        } else {
            // Input is empty, stop typing
            onSetTyping(conversation.id, false)
        }

        return () => {
            if (typingTimeout) clearTimeout(typingTimeout)
        }
    }, [messageInput, conversation, onSetTyping])

    const handleSend = () => {
        if (!messageInput.trim() && attachments.length === 0) return
        onSendMessage(messageInput, attachments)
        setMessageInput('')
        setAttachments([])
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    if (!conversation) {
        return (
            <div className="text-center py-5 text-muted">
                <i className="mdi mdi-message-outline" style={{ fontSize: '64px' }}></i>
                <h5 className="mt-3">Select a conversation</h5>
                <p>Choose a conversation from the list to start chatting</p>
            </div>
        )
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header */}
            <div className="chat-header">
                <div className="user-info">
                    <h5>{conversation.customer_name || 'Anonymous'}</h5>
                    <p>{conversation.customer_email}</p>
                </div>
                <div className="actions">
                    <Button
                        color="light"
                        size="sm"
                        onClick={() => setShowTicketForm(true)}
                        title="Create Ticket"
                        className="btn-icon"
                    >
                        <i className="mdi mdi-ticket-outline"></i>
                    </Button>
                    <Button
                        color="light"
                        size="sm"
                        onClick={() => setShowTransferModal(true)}
                        title="Transfer"
                        className="btn-icon"
                    >
                        <i className="mdi mdi-account-switch-outline"></i>
                    </Button>
                    <Button
                        color="light"
                        size="sm"
                        onClick={onCloseConversation}
                        title="Close"
                        className="text-danger btn-icon"
                    >
                        <i className="mdi mdi-close"></i>
                    </Button>
                </div>
            </div>

            {/* Messages */}
            <div className="messages-container">
                {conversation.messages && conversation.messages.length > 0 ? (
                    conversation.messages.map((message, index) => {
                        // System messages
                        if (message.sender_type === 'system') {
                            return (
                                <div key={message.id || index} className="message-group system">
                                    <div className="system-bubble">
                                        <i className="mdi mdi-information-outline me-1"></i> {message.message}
                                    </div>
                                </div>
                            )
                        }

                        // Regular messages
                        const isAgent = message.sender_type === 'agent'
                        return (
                            <div
                                key={message.id || index}
                                className={`message-group ${isAgent ? 'agent' : 'customer'}`}
                            >
                                <div className={`message-bubble ${isAgent ? 'agent' : 'customer'}`}>
                                    <div className="message-text">{message.message}</div>

                                    {message.attachments && Array.isArray(message.attachments) && message.attachments.length > 0 && (
                                        <div className="mt-2">
                                            {message.attachments.map((attachment, idx) => (
                                                <div key={idx} className="mb-1">
                                                    <a
                                                        href={attachment.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="d-flex align-items-center gap-1"
                                                    >
                                                        <i className="mdi mdi-paperclip"></i>
                                                        <span style={{ textDecoration: 'underline' }}>{attachment.name}</span>
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="message-meta">
                                        <span>{moment(message.created_at).format('h:mm A')}</span>
                                        {isAgent && (
                                            <span>
                                                {message.read_at ? <i className="mdi mdi-check-all"></i> : <i className="mdi mdi-check"></i>}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })
                ) : (
                    <div className="empty-state">
                        <i className="mdi mdi-chat-processing-outline"></i>
                        <h5>No messages yet</h5>
                        <p>Start the conversation by sending a message.</p>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {conversation && typingUsers && typingUsers[conversation.id]?.isTyping && (
                <div className="px-4 py-2 bg-light border-top">
                    <small className="text-muted fst-italic">
                        <i className="mdi mdi-pencil me-1"></i> {typingUsers[conversation.id].userName} is typing...
                    </small>
                </div>
            )}

            {/* Input Area */}
            <div className="input-area">
                {attachments.length > 0 && (
                    <div className="attachments-preview">
                        {attachments.map((att, idx) => (
                            <div key={idx} className="attachment-item">
                                <i className="mdi mdi-file-outline"></i> {att.name}
                                <button onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}>
                                    <i className="mdi mdi-close"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="input-wrapper">
                    <div className="input-actions">
                        <FileUpload
                            onFileUploaded={(file) => setAttachments([...attachments, file])}
                            disabled={!conversation}
                            render={(open) => (
                                <Button color="link" className="text-muted p-1" onClick={open} title="Attach File">
                                    <i className="mdi mdi-paperclip" style={{ fontSize: '1.2rem' }}></i>
                                </Button>
                            )}
                        />
                        <CannedResponseSelector
                            onSelect={(content) => setMessageInput(messageInput + content)}
                            disabled={!conversation}
                            render={(open) => (
                                <Button color="link" className="text-muted p-1" onClick={open} title="Canned Responses">
                                    <i className="mdi mdi-lightning-bolt" style={{ fontSize: '1.2rem' }}></i>
                                </Button>
                            )}
                        />
                    </div>

                    <Input
                        type="textarea"
                        placeholder="Type your message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyPress={handleKeyPress}
                    />

                    <Button
                        color="primary"
                        className="btn-rounded px-3"
                        onClick={handleSend}
                        disabled={!messageInput.trim() && attachments.length === 0}
                    >
                        <i className="mdi mdi-send"></i>
                    </Button>
                </div>
            </div>

            {/* Modals */}
            <TransferModal
                isOpen={showTransferModal}
                toggle={() => setShowTransferModal(false)}
                onTransfer={(agentId) => {
                    if (onTransferConversation) {
                        onTransferConversation(conversation.id, agentId)
                    }
                    setShowTransferModal(false)
                }}
                currentAgentId={conversation?.assigned_agent_id}
            />

            <TicketForm
                isOpen={showTicketForm}
                toggle={() => setShowTicketForm(false)}
                conversationId={conversation?.id}
                onSuccess={(ticket) => {
                    console.log('Ticket created:', ticket)
                    alert(`Ticket ${ticket.ticket_number} created successfully!`)
                    setShowTicketForm(false)
                }}
            />
        </div>
    )
}

export default MessagePanel
